import dayjs, { Dayjs } from 'dayjs';
import {
	UserAvailabilityPayload,
	createUserAvailability,
	deleteUserAvailability,
	updateUserAvailability,
} from 'core/API/userAvailabilities';
import { UserAvailability } from 'core/interfaces/userInterface';
import {
	WeekDay,
	extractDayNumericId,
	getLatestAvailabilityForDay,
	normalizeDayIri,
	parseAvailabilityMoment,
} from 'module/schedule/utils/availabilityDisplay';
import { isProductLicensed } from 'module/dashboard/utils/homePage';

export type OnboardingDayAvailability = {
	day: WeekDay;
	enabled: boolean;
	startTime: Dayjs | null;
	endTime: Dayjs | null;
};

export type OnboardingStepKey =
	| 'basicInfo'
	| 'availability'
	| 'documents'
	| 'submit'
	| 'complete';

export type OnboardingStepIndices = {
	basicInfo: number;
	availability: number | null;
	documents: number | null;
	submit: number;
	complete: number | null;
};

/** True when the organization has an active Scheduler (Scheduling Module) license. */
export function isSchedulingModuleLicensed(
	products: Array<{ label: string }> | null | undefined,
): boolean {
	return isProductLicensed(products || [], 'Scheduler');
}

export function buildOnboardingStepLabels(options: {
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): string[] {
	const { hasDocuments, schedulingLicensed } = options;
	const steps = ['Basic Information'];

	if (schedulingLicensed) {
		steps.push('Availability');
	}

	if (hasDocuments) {
		steps.push('Fill Documents', 'Review & Submit');
	} else {
		steps.push('Submit', 'Complete');
	}

	return steps;
}

export function resolveOnboardingStepIndices(options: {
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): OnboardingStepIndices {
	const { hasDocuments, schedulingLicensed } = options;
	let next = 0;
	const basicInfo = next++;
	const availability = schedulingLicensed ? next++ : null;
	const documents = hasDocuments ? next++ : null;
	const submit = next++;
	const complete = hasDocuments ? null : next;

	return { basicInfo, availability, documents, submit, complete };
}

export function resolveInitialOnboardingStep(options: {
	basicInfoCompleted: boolean;
	availabilityCompleted: boolean;
	allDocumentsCompleted: boolean;
	isSubmitted: boolean;
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): number {
	const indices = resolveOnboardingStepIndices(options);
	const {
		basicInfoCompleted,
		availabilityCompleted,
		allDocumentsCompleted,
		isSubmitted,
		schedulingLicensed,
	} = options;

	if (!basicInfoCompleted) {
		return indices.basicInfo;
	}

	if (schedulingLicensed && !availabilityCompleted && indices.availability != null) {
		return indices.availability;
	}

	if (options.hasDocuments) {
		if (!allDocumentsCompleted && indices.documents != null) {
			return indices.documents;
		}
		return indices.submit;
	}

	if (isSubmitted && indices.complete != null) {
		return indices.complete;
	}

	return indices.submit;
}

export function toWorkplaceIri(workplaceId: number | string): string {
	const id = String(workplaceId).replace(/^\/?api\/workplaces\//, '');
	return `/api/workplaces/${id}`;
}

export function toDayIri(day: WeekDay | string | number): string {
	if (typeof day === 'string') {
		const normalized = normalizeDayIri(day);
		return normalized.startsWith('api/') ? `/${normalized}` : normalized.startsWith('/') ? normalized : `/api/days/${normalized}`;
	}
	if (typeof day === 'number') {
		return `/api/days/${day}`;
	}
	if (day['@id']) {
		const normalized = normalizeDayIri(day['@id']);
		return normalized.startsWith('/') ? normalized : `/${normalized}`;
	}
	const numericId = extractDayNumericId(day);
	if (numericId == null) {
		throw new Error('Unable to resolve day IRI for availability');
	}
	return `/api/days/${numericId}`;
}

/**
 * Formats TimePicker values as HH:mm:ss, matching AvailabilityForm / shift request payloads.
 */
export function toAvailabilityTimeString(time: Dayjs): string {
	return dayjs(time).format('HH:mm:ss');
}

export function buildUserAvailabilityPayload(options: {
	workplaceId: number | string;
	day: WeekDay;
	startTime: Dayjs;
	endTime: Dayjs;
}): UserAvailabilityPayload {
	const { workplaceId, day, startTime, endTime } = options;
	return {
		workplace: toWorkplaceIri(workplaceId),
		day: toDayIri(day),
		startAt: toAvailabilityTimeString(startTime),
		endAt: toAvailabilityTimeString(endTime),
	};
}

export function hasSavedAvailability(
	currentAvailability: UserAvailability[] | null | undefined,
): boolean {
	return (currentAvailability || []).some((entry) => {
		if (!entry?.startAt || !entry?.endAt) return false;
		const start = parseAvailabilityMoment(entry.startAt);
		const end = parseAvailabilityMoment(entry.endAt);
		if (!start || !end) return false;
		return start.format('HH:mm') !== end.format('HH:mm');
	});
}

/**
 * Persists each day's availability to user_availabilities:
 * - enabled + times → create or update
 * - previously set day now disabled → delete existing record
 */
export async function saveOnboardingAvailability(options: {
	workplaceId: number | string;
	days: OnboardingDayAvailability[];
	currentAvailability: UserAvailability[];
}): Promise<void> {
	const { workplaceId, days, currentAvailability } = options;
	const operations: Promise<unknown>[] = [];

	for (const dayAvailability of days) {
		const existing = getLatestAvailabilityForDay(currentAvailability, dayAvailability.day);

		if (dayAvailability.enabled) {
			if (!dayAvailability.startTime || !dayAvailability.endTime) {
				continue;
			}
			const payload = buildUserAvailabilityPayload({
				workplaceId,
				day: dayAvailability.day,
				startTime: dayAvailability.startTime,
				endTime: dayAvailability.endTime,
			});

			if (existing?.id != null) {
				operations.push(updateUserAvailability(existing.id, payload));
			} else {
				operations.push(createUserAvailability(payload));
			}
			continue;
		}

		if (existing?.id != null) {
			operations.push(deleteUserAvailability(existing.id));
		}
	}

	await Promise.all(operations);
}
